// Helps keep track of nested Expressions and Combiners
import moment from 'moment';
import { and } from '../../hooks/use-form-item/validators/compositions';
import { maxLength } from '../../hooks/use-form-item/validators/operational';
import { DateUtils } from '../../utils/DateUtils';
import { Combiner } from './components/Combiner';
import { FilterValueInputType } from './enum/FilterValueInputType';
import { Operation } from './enum/Operation';
import { Operator } from './enum/Operator';
import { DateTimeExpression, Expression, FilterConfiguration, OperationValue, Option, SelectExpression, StringExpression, TimestampExpression } from './types';

let id = 1;
export const getUniqueId = () => id++;

export const getDefaultCombiner = (): Combiner => {
  return { operator: Operator.AND, subFilters: [], id: getUniqueId() };
};

export const getDefaultExpression = (filter: FilterConfiguration): Expression => {
  let value: Expression['value'] = '';
  switch (filter.type) {
    case FilterValueInputType.DATE_TIME:
    case FilterValueInputType.TIMESTAMP:
      value = moment();
      break;
    case FilterValueInputType.SELECT:
    case FilterValueInputType.AUTOCOMPLETE:
      value = filter.options[0].value;
      break;
  }

  return { id: getUniqueId(), operation: filter.operations[0].value, field: filter.filterKey, value } as Expression;
};

const TRUNCATE_LENGTH = 12;

export const getChipLabel = (expression: Expression, filters: FilterConfiguration[], truncate = true) => {
  // We are certain that we have this filter configuration
  const filter = filters.find((f) => f.filterKey === expression.field) as FilterConfiguration;
  const operation = filter.operations.find((op) => op.value === expression.operation) as OperationValue;

  let value: string;
  switch (filter.type) {
    case FilterValueInputType.DATE_TIME:
      value = DateUtils.toDateTimeString((expression as DateTimeExpression).value);
      break;
    case FilterValueInputType.TIMESTAMP:
      value = DateUtils.toTimestampString((expression as TimestampExpression).value);
      break;
    case FilterValueInputType.SELECT: {
      const option = filter.options.find((opt) => opt.value === (expression as SelectExpression).value) as Option;
      value = option.label;
      break;
    }
    case FilterValueInputType.STRING:
    default:
      value = (expression as StringExpression).value || '...';
      break;
  }

  if (truncate && value.length > TRUNCATE_LENGTH) {
    value = `${value.slice(0, TRUNCATE_LENGTH)}...`;
  }

  return `${filter.label} ${operation.label} ${value}`;
};

export const expressionValidator = ({ type, validator }: FilterConfiguration, operation: Operation, value: Expression['value']) => {
  if ([FilterValueInputType.DATE_TIME, FilterValueInputType.TIMESTAMP].includes(type)) {
    // Date and timestamp filters currently do not support validation
    return true;
  }
  // Regardless of explicit validators, enforce max-length checks to protect the API
  if (!validator) {
    return maxLength(1000)(value);
  }
  return and(validator(operation), maxLength(1000))(value);
};
